import { Component, Input, OnInit } from '@angular/core';
import { StarRatingComponent } from './star-rating.component';
import { ActionButtonComponent } from '../buttons/action-button.component';
import { BorderIconButtonComponent } from '../buttons/border-icon-button.component';
import { BottomSheetService } from '../../services/bottom-sheet.service';
import { TranslateService } from '../../services/translate.service';
import { ReviewService } from '../../services/review.service';
import { firstValueFrom } from 'rxjs';
import { SubReviewsConfig } from '../../models/FSConfig';

@Component({
  standalone: true,
  imports: [StarRatingComponent, ActionButtonComponent, BorderIconButtonComponent],
  selector: 'app-review-detail-sheet',
  styles: [`
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.5); }
      to { opacity: 1; transform: scale(1); }
    }
    .animate-scale-in {
      animation: scaleIn 0.4s ease-out both;
    }
  `],
  template: `
    <div class="flex flex-col gap-8 pb-8">
      <div class="flex justify-center">
        <app-star-rating [rating]="overallRating" [disabled]="reviewExpired || reviewLocked" (ratingChange)="overallRating = $event" size="lg"></app-star-rating>
      </div>
      @if (reviewExpired) {
        <div class="flex flex-col items-center gap-6">
          <div class="w-20 h-20 rounded-full bg-theme-primary flex items-center justify-center animate-scale-in">
            <i class="fa-solid fa-clock text-theme-contrast text-3xl"></i>
          </div>
          <div class="text-2xl font-bold text-theme-text text-center">
            {{ getString(reviewService.expiredText) }}
          </div>
        </div>
        <div class="mt-4 flex flex-col gap-3">
          @if (showGoogleButton) {
            <app-action-button
              [text]="getString('Betygsätt på Google')"
              icon="fa-arrow-up-right-from-square"
              [chevron]="false"
              (onClick)="openGoogleAndClose()">
            </app-action-button>
          }
          <app-border-icon-button
            [text]="getString('Stäng')"
            (onClick)="close()">
          </app-border-icon-button>
        </div>
      } @else if (ready) {
        @if (reviewService.showFood) {
          <div class="flex items-center justify-between">
            <span class="text-lg font-medium">{{ getString('Mat & Dryck') }}</span>
            <app-star-rating [rating]="ratingFood" [disabled]="reviewLocked" (ratingChange)="ratingFood = $event" size="md"></app-star-rating>
          </div>
        }
        @if (reviewService.showService) {
          <div class="flex items-center justify-between">
            <span class="text-lg font-medium">{{ getString('Service') }}</span>
            <app-star-rating [rating]="ratingService" [disabled]="reviewLocked" (ratingChange)="ratingService = $event" size="md"></app-star-rating>
          </div>
        }
        @if (reviewService.showAmbiance) {
          <div class="flex items-center justify-between">
            <span class="text-lg font-medium">{{ getString('Atmosfär') }}</span>
            <app-star-rating [rating]="ratingAmbiance" [disabled]="reviewLocked" (ratingChange)="ratingAmbiance = $event" size="md"></app-star-rating>
          </div>
        }
        @if (reviewService.showValue) {
          <div class="flex items-center justify-between">
            <span class="text-lg font-medium">{{ getString('Prisvärdhet') }}</span>
            <app-star-rating [rating]="ratingValue" [disabled]="reviewLocked" (ratingChange)="ratingValue = $event" size="md"></app-star-rating>
          </div>
        }

        <div class="mt-4 flex flex-col gap-3">
          @if (showGoogleButton) {
            <app-action-button
              [text]="getString('Betygsätt på Google')"
              icon="fa-arrow-up-right-from-square"
              [chevron]="false"
              [spinner]="isSubmitting"
              [disabled]="isSubmitting"
              (onClick)="submitAndOpenGoogle()">
            </app-action-button>
          }
          @if (reviewService.hasSubReviews) {
            <app-border-icon-button
              [text]="getString('Spara och stäng')"
              [disabled]="isSubmitting"
              (onClick)="submitAndClose()">
            </app-border-icon-button>
          }
        </div>
      }
    </div>
  `
})
export class ReviewDetailSheetComponent implements OnInit {
  @Input() sheetData: {
    overallRating: number,
    type: 'qr_code' | 'self_service',
    receiptKey?: string,
    ratingFood?: number,
    ratingService?: number,
    ratingAmbiance?: number,
    ratingValue?: number,
    subReviews?: SubReviewsConfig
  } | null = null;

  overallRating = 0;
  ratingFood = 0;
  ratingService = 0;
  ratingAmbiance = 0;
  ratingValue = 0;
  isSubmitting = false;
  reviewLocked = false;
  reviewExpired = false;
  ready = false;
  showGoogleButton = false;

  constructor(
    public reviewService: ReviewService,
    private sheetService: BottomSheetService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    if (this.sheetData) {
      this.overallRating = this.sheetData.overallRating ?? 0;
      this.ratingFood = this.sheetData.ratingFood ?? 0;
      this.ratingService = this.sheetData.ratingService ?? 0;
      this.ratingAmbiance = this.sheetData.ratingAmbiance ?? 0;
      this.ratingValue = this.sheetData.ratingValue ?? 0;
      this.showGoogleButton = this.reviewService.canShowGoogleReview(this.overallRating);

      if (this.sheetData.receiptKey && this.overallRating > 0) {
        try {
          await firstValueFrom(this.reviewService.submitReview({
            date: new Date().toISOString(),
            type: this.sheetData.type ?? 'qr_code' as const,
            rating: this.overallRating,
            rating_food: this.ratingFood,
            rating_service: this.ratingService,
            rating_ambiance: this.ratingAmbiance,
            rating_value: this.ratingValue,
            receipt_key: this.sheetData.receiptKey
          }));
          this.ready = true;
        } catch (e: any) {
          if (e?.status === 403 || e?.status === 410) {
            this.reviewExpired = true;
          } else {
            this.ready = true;
          }
        }
      } else {
        this.ready = true;
      }
    }
  }

  getString(s: string): string {
    return this.translate.getString(s);
  }

  private async submitReview() {
    if (!this.sheetData?.receiptKey || this.reviewLocked) return;
    try {
      await firstValueFrom(this.reviewService.submitReview({
        date: new Date().toISOString(),
        type: this.sheetData.type ?? 'qr_code' as const,
        rating: this.overallRating,
        rating_food: this.ratingFood,
        rating_service: this.ratingService,
        rating_ambiance: this.ratingAmbiance,
        rating_value: this.ratingValue,
        receipt_key: this.sheetData.receiptKey
      }));
    } catch (e: any) {
      console.warn('Review submission failed:', e);
      if (e?.status === 403 || e?.status === 410) {
        this.reviewExpired = true;
      }
    }
  }

  async submitAndClose() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      await this.submitReview();
    } finally {
      this.isSubmitting = false;
    }
    this.sheetService.clearStack();
  }

  close() {
    this.sheetService.clearStack();
  }

  openGoogleAndClose() {
    this.sheetService.clearStack();
    const url = this.reviewService.getGoogleReviewUrl();
    if (url) window.open(url, '_blank');
  }

  async submitAndOpenGoogle() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      await this.submitReview();
    } finally {
      this.isSubmitting = false;
    }
    this.sheetService.clearStack();
    const url = this.reviewService.getGoogleReviewUrl();
    if (url) window.open(url, '_blank');
  }
}
